import React from 'react';
import i18n from 'i18next';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level error boundary that catches rendering errors and shows a friendly neumorphic error UI.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[VO2Peaks] Unhandled render error:', error, info);
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div style={styles.root}>
          <div style={styles.card}>
            <div style={styles.icon}>⚠</div>
            <h3 style={styles.title}>{i18n.t('errorBoundary.title')}</h3>
            <p style={styles.message}>
              {this.state.error?.message ?? i18n.t('errorBoundary.fallbackMessage')}
            </p>
            <button
              style={styles.btn}
              onClick={() => this.setState({ hasError: false, error: null })}
            >
              {i18n.t('errorBoundary.tryAgain')}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const styles: Record<string, React.CSSProperties> = {
  root: {
    width: '100%',
    height: '100%',
    background: 'var(--surface-0)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    background: 'var(--surface-0)',
    boxShadow: 'var(--nm-raised-lg)',
    borderRadius: 'var(--r-tile)',
    padding: 48,
    maxWidth: 400,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
  },
  icon: { fontSize: 48, color: 'var(--color-danger)' },
  title: { fontSize: 'var(--fs-h3)', color: 'var(--fg-1)', margin: 0 },
  message: {
    fontSize: 'var(--fs-small)',
    color: 'var(--fg-3)',
    margin: 0,
    fontFamily: 'var(--font-mono)',
  },
  btn: {
    background: 'linear-gradient(135deg, var(--vo2-pink-400), var(--vo2-pink-600))',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--r-pill)',
    padding: '10px 28px',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: 'var(--nm-primary)',
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--fs-body)',
  },
};
